import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Article, ArticleCategory, ImpactLevel } from "@/data/mockData";

interface DBArticle {
  id: string;
  slug: string;
  headline: string;
  excerpt: string;
  content: string;
  category: string;
  impact_level: string;
  author: string;
  organization: string;
  entity_name: string | null;
  read_time: string | null;
  rating: number | null;
  versus: string[] | null;
  created_at: string;
  banner_url: string | null;
  banner_alt_text: string | null;
  banner_caption: string | null;
  show_banner: boolean | null;
}

function mapDBtoArticle(row: DBArticle): Article {
  return {
    id: row.id,
    slug: row.slug,
    headline: row.headline,
    excerpt: row.excerpt,
    content: row.content,
    category: row.category as ArticleCategory,
    impactLevel: row.impact_level as ImpactLevel,
    author: row.author,
    organization: row.organization,
    entityName: row.entity_name ?? undefined,
    readTime: row.read_time ?? undefined,
    rating: row.rating ?? undefined,
    createdAt: row.created_at,
    versus: row.versus as [string, string] | undefined,
    bannerUrl: row.banner_url ?? undefined,
    bannerAltText: row.banner_alt_text ?? undefined,
    bannerCaption: row.banner_caption ?? undefined,
    showBanner: row.show_banner ?? false,
  };
}

const PAGE_SIZE = 10;

export function useArticles(category?: ArticleCategory | "all") {
  return useQuery({
    queryKey: ["articles", category],
    queryFn: async () => {
      let query = supabase
        .from("articles")
        .select("*")
        .order("created_at", { ascending: false });

      if (category && category !== "all") {
        query = query.eq("category", category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown as DBArticle[]).map(mapDBtoArticle);
    },
  });
}

/** Paginated articles for homepage – returns first PAGE_SIZE, then more on demand */
export function usePaginatedArticles(category?: ArticleCategory | "all") {
  return useInfiniteQuery({
    queryKey: ["articles-paginated", category],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from("articles")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);

      if (category && category !== "all") {
        query = query.eq("category", category);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      const articles = (data as unknown as DBArticle[]).map(mapDBtoArticle);
      return { articles, total: count ?? 0, page: pageParam };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      if (nextPage * PAGE_SIZE >= lastPage.total) return undefined;
      return nextPage;
    },
  });
}

/** Paginated articles for category archive pages */
export function useCategoryArchive(category: ArticleCategory | "all", pageSize = 12) {
  return useInfiniteQuery({
    queryKey: ["category-archive", category, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from("articles")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(pageParam * pageSize, (pageParam + 1) * pageSize - 1);

      if (category !== "all") {
        query = query.eq("category", category);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      const articles = (data as unknown as DBArticle[]).map(mapDBtoArticle);
      return { articles, total: count ?? 0, page: pageParam };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      if (nextPage * pageSize >= lastPage.total) return undefined;
      return nextPage;
    },
  });
}

export function useArticleBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["article", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return mapDBtoArticle(data as unknown as DBArticle);
    },
    enabled: !!slug,
  });
}

export function useHighImpactArticles(limit = 10) {
  return useQuery({
    queryKey: ["articles", "high-impact", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("impact_level", "high")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as unknown as DBArticle[]).map(mapDBtoArticle);
    },
  });
}

export function useRelatedArticles(category: ArticleCategory, excludeId: string, limit = 3) {
  return useQuery({
    queryKey: ["articles", "related", category, excludeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("category", category)
        .neq("id", excludeId)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as unknown as DBArticle[]).map(mapDBtoArticle);
    },
  });
}

export function useArticlesByCategory(category: ArticleCategory, limit = 50) {
  return useQuery({
    queryKey: ["articles", "by-category", category, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("category", category)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as unknown as DBArticle[]).map(mapDBtoArticle);
    },
  });
}

export function useArticlesByEntityName(entityName: string | undefined, limit = 5) {
  return useQuery({
    queryKey: ["articles", "by-entity", entityName, limit],
    queryFn: async () => {
      if (!entityName) return [];
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("entity_name", entityName)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data as unknown as DBArticle[]).map(mapDBtoArticle);
    },
    enabled: !!entityName,
  });
}
